import { z } from "zod";
/**
 * Places schemas — shared contract between apps/web hooks and the
 * apps/api/src/routes/places/places.ts Hono routes. ALL places data access (reads and
 * writes) goes through the backend; these schemas validate the write bodies on
 * both sides (docs/BACKEND_API.md; Places plan §1.2).
 */
const ownershipStatusEnum = z.enum([
    "owned",
    "long_term_lease",
    "short_term_rental",
    "partnership_in_kind",
    "public_space_permit",
    "other",
]);
const premisesLevelEnum = z.enum(["site", "building", "section"]);
/**
 * Tenure arrangement (ownership/lease) — financial, Pattern C only.
 * The validity interval IS the lease term (valid_to NULL = open-ended).
 * Cost + period are required for lease/rental tenure types.
 */
export const tenureSchema = z
    .object({
    id: z.string().uuid().optional(),
    place_id: z.string().uuid().optional(),
    tenure_type: ownershipStatusEnum,
    valid_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD"),
    valid_to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD")
        .nullable()
        .optional(),
    cost: z.number().finite().nonnegative().nullable().optional(),
    cost_period: z.enum(["monthly", "quarterly", "annually"]).nullable().optional(),
    currency: z.string().length(3).nullable().optional(),
    landlord_person_id: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .refine((t) => !t.valid_to || t.valid_to >= t.valid_from, { message: "valid_to must be on or after valid_from", path: ["valid_to"] })
    .refine((t) => !["long_term_lease", "short_term_rental"].includes(t.tenure_type) ||
    (t.cost != null && t.cost_period != null), {
    message: "cost and cost_period are required for lease/rental tenures",
    path: ["cost"],
});
/** POST /api/v1/places/orgs/:orgId/places → create_place RPC (Pattern C). */
export const createPlaceSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(5000).nullable().optional(),
    bedes_occupancy_classification: z.string().min(1).max(200),
    gis_location_id: z.string().uuid().nullable().optional(),
    parent_place_id: z.string().uuid().nullable().optional(),
    premises_level: premisesLevelEnum.default("building"),
    gross_floor_area_source_value: z.number().finite().positive().nullable().optional(),
    gross_floor_area_source_unit: z.enum(["m2", "ft2"]).nullable().optional(),
    // year_built / occupancy_pct are place-scoped DETAILS (place_details rows,
    // R3 profile-fact consolidation); bedes_covered_area_type dissolved (R4 —
    // floor-area basis is detail-definition identity, not a value + side flag).
    construction_status: z.enum(["existing", "project", "test"]).default("existing"),
    ubid: z.string().max(200).nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    /** Optional initial tenure — ignored for sections (they inherit). */
    tenure: tenureSchema.nullable().optional(),
})
    .refine((p) => p.premises_level !== "section" || !!p.parent_place_id, { message: "a section requires a parent place", path: ["parent_place_id"] });
/**
 * PATCH /api/v1/places/orgs/:orgId/places/:placeId — scalar columns only
 * (Pattern B; hierarchy/tenure/use changes have their own endpoints).
 */
export const updatePlaceSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(5000).nullable().optional(),
    gis_location_id: z.string().uuid().nullable().optional(),
    gross_floor_area_source_value: z.number().finite().positive().nullable().optional(),
    gross_floor_area_source_unit: z.enum(["m2", "ft2"]).nullable().optional(),
    construction_status: z.enum(["existing", "project", "test"]).optional(),
    ubid: z.string().max(200).nullable().optional(),
    is_active: z.boolean().optional(),
    notes: z.string().max(5000).nullable().optional(),
    /** Place-level emission-region default (places_emissions) — beats the
     * postal/country derivation, is beaten by a per-meter override. */
    emission_region_code: z.string().max(20).nullable().optional(),
})
    .refine((p) => Object.keys(p).length > 0, { message: "empty update" });
/** DELETE /api/v1/places/orgs/:orgId/places/:placeId (body). */
export const deletePlaceSchema = z
    .object({
    mode: z.enum(["reassign", "delete_children"]),
    newParentId: z.string().uuid().nullable().optional(),
});
/** POST/PATCH place uses (Pattern B). */
export const upsertPlaceUseSchema = z.object({
    id: z.string().uuid().optional(),
    bedes_occupancy_classification: z.string().min(1).max(200),
    is_primary: z.boolean().default(false),
    is_active: z.boolean().default(true),
    notes: z.string().max(2000).nullable().optional(),
});
/** POST/PATCH place contacts (Pattern B). */
export const upsertPlaceContactSchema = z.object({
    id: z.string().uuid().optional(),
    person_id: z.string().uuid(),
    bedes_contact_label: z.string().min(1).max(200),
    is_primary: z.boolean().default(false),
    is_active: z.boolean().default(true),
    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
});
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
const detailValueFields = {
    value_numeric: z.number().finite().nullable().optional(),
    value_text: z.string().max(2000).nullable().optional(),
    value_boolean: z.boolean().nullable().optional(),
    value_date: isoDate.nullable().optional(),
};
const countDetailValues = (d) => [d.value_numeric, d.value_text, d.value_boolean, d.value_date].filter((v) => v != null).length;
/**
 * POST /orgs/:orgId/places/:placeId/details — record a CHANGE (a new
 * effective-dated row) or a RETIREMENT tombstone (`is_retired: true`, no value).
 * Corrections go through PATCH on the existing row instead
 * (docs/places/phase-2.md effective-dating semantics).
 */
export const createPlaceDetailSchema = z
    .object({
    definition_id: z.string().uuid(),
    /** Required for use-scoped definitions (the DB trigger enforces scope). */
    place_use_id: z.string().uuid().nullable().optional(),
    current_as_of: isoDate,
    ...detailValueFields,
    unit: z.string().max(100).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    is_estimated: z.boolean().default(false),
    is_retired: z.boolean().default(false),
    source: z
        .enum(["org_reported", "venue_confirmed", "unconfirmed"])
        .default("org_reported"),
})
    .refine((d) => (d.is_retired ? countDetailValues(d) === 0 : countDetailValues(d) === 1), {
    message: "exactly one value_* field is required (none for a retirement tombstone)",
    path: ["value_numeric"],
});
/**
 * PATCH /orgs/:orgId/places/:placeId/details/:detailId — a CORRECTION: fix the
 * existing row in place (the DB validation trigger re-checks the value shape).
 */
export const updatePlaceDetailSchema = z
    .object({
    current_as_of: isoDate.optional(),
    ...detailValueFields,
    unit: z.string().max(100).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    is_estimated: z.boolean().optional(),
    /** A correction may retire the row IN PLACE (null the value column too) —
     * the only removal path for a value recorded today, since a second row on
     * the same as-of date would violate the unique key. The mirror image also
     * holds: re-entering a value on the day it was retired must REVIVE the
     * tombstone in place (is_retired: false + the value column) — a new row
     * would collide with the tombstone on the same unique key. The revive
     * refine below keeps the shape coherent; the same-day-only POLICY (a
     * backdated re-entry is a new row, not an un-retire) is owned by the
     * client policy layer (saveDetail) — the API trusts correction callers,
     * same as it does for value/as-of rewrites. */
    is_retired: z.boolean().optional(),
    source: z.enum(["org_reported", "venue_confirmed", "unconfirmed"]).optional(),
})
    .refine((d) => Object.keys(d).length > 0, { message: "empty update" })
    // A correction may swap which value column is set (e.g. enum → text never
    // happens, but numeric value fixes do); sending more than one non-null value
    // is always a client bug the DB would also reject.
    .refine((d) => countDetailValues(d) <= 1, {
    message: "at most one value_* field per correction",
    path: ["value_numeric"],
})
    .refine((d) => !d.is_retired || countDetailValues(d) === 0, {
    message: "a retirement carries no value",
    path: ["is_retired"],
})
    // A revive without a value would only die later on the DB presence CHECK
    // with a raw constraint message — reject it here with a real one.
    .refine((d) => d.is_retired !== false || countDetailValues(d) === 1, {
    message: "reviving a retired value requires exactly one value_* field",
    path: ["is_retired"],
});
/**
 * A link's date range must not run backwards. Only checked when BOTH ends are
 * present in the payload — a patch that touches one end alone can't be judged
 * here (design audit 2026-08-09: neither side enforced this, so a reversed
 * range stored silently). ISO yyyy-MM-dd compares correctly as a string.
 */
const linkDatesOrdered = (v) => !(v.start_date && v.end_date) || v.start_date <= v.end_date;
const LINK_DATES_MESSAGE = {
    message: "end_date must not be before start_date",
    path: ["end_date"],
};
/**
 * Why a project uses a place — the `project_places.role` vocabulary (the DB
 * CHECK on the column holds the same list). One list for the
 * attach + patch schemas and the web's role picker / column resolver.
 */
export const PROJECT_PLACE_ROLES = [
    "venue",
    "event",
    "rehearsal",
    "office",
    "production",
    "storage",
    "other",
];
export const projectPlaceRoleSchema = z.enum(PROJECT_PLACE_ROLES);
/** Max length of a project↔place link's free-text notes (shared with the web form). */
export const PROJECT_PLACE_NOTES_MAX = 2000;
/**
 * POST /orgs/:orgId/projects/:projectId/places — attach a place (Pattern B+).
 * Strict: an unknown key is a 400, never silently dropped. The date-order
 * refine catches a reversed range in one payload; a range made invalid against
 * a STORED end is the DB CHECK's coded `invalid_date_range` 400.
 */
export const attachProjectPlaceSchema = z
    .object({
    place_id: z.string().uuid(),
    role: projectPlaceRoleSchema.default("venue"),
    is_primary: z.boolean().default(false),
    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    notes: z.string().max(PROJECT_PLACE_NOTES_MAX).nullable().optional(),
})
    .strict()
    .refine(linkDatesOrdered, LINK_DATES_MESSAGE);
/** Patch for an existing project↔place link (M5.7). Primary changes go
 * through the dedicated set-primary endpoint (atomic proc), not this patch —
 * an `is_primary` key is therefore an unknown key (strict → 400). */
export const updateProjectPlaceSchema = z
    .object({
    role: projectPlaceRoleSchema.optional(),
    is_active: z.boolean().optional(),
    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    notes: z.string().max(PROJECT_PLACE_NOTES_MAX).nullable().optional(),
})
    .strict()
    .refine((p) => Object.keys(p).length > 0, { message: "empty patch" })
    .refine(linkDatesOrdered, LINK_DATES_MESSAGE);
//# sourceMappingURL=places.js.map